import { CSSResult, html } from 'lit';
import { property } from 'lit/decorators.js';

import { Component } from '../../models';
import { DisabledMixin } from '../../utils/mixins/DisabledMixin';

import styles from './staticradio.styles';

/**
 * The StaticRadio component is a decorative, non-interactive radio button used for visual
 * presentation in read-only scenarios. Unlike the interactive `mdc-radio`, it does not
 * handle user interactions or form submissions.
 *
 * This component supports multiple visual states: checked, disabled, readonly, and soft-disabled.
 *
 * ## When to use
 * Use StaticRadio to display radio button states in read-only contexts such as summary views,
 * confirmation screens, or when showing historical form data. For interactive radio buttons, use
 * `mdc-radio` instead.
 *
 * ## Accessibility
 * - StaticRadio is decorative and non-interactive by design
 * - Always pair with descriptive text labels in the default slot
 * - Do not use this as a replacement for interactive radio buttons in forms
 *
 * @tagname mdc-staticradio
 *
 * @cssproperty --mdc-staticradio-inner-circle-size - Size of the inner circle when checked.
 * @cssproperty --mdc-staticradio-outer-circle-size - Size of the outer circle border.
 * @cssproperty --mdc-staticradio-inner-circle-background-color - Background color of the inner circle when checked.
 * @cssproperty --mdc-staticradio-outer-circle-border-color - Border color of the outer circle.
 * @cssproperty --mdc-staticradio-outer-circle-background-color - Background color of the outer circle.
 *
 * @csspart radio-icon - The radio icon element.
 *
 * @slot - Default slot for the label of the radio.
 */
class StaticRadio extends DisabledMixin(Component) {
  /**
   * Determines whether the radio appears checked (selected) or unchecked.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) checked = false;

  /**
   * Determines whether the radio appears in a read-only state.
   * Note: This is purely visual as StaticRadio is non-interactive by design.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) readonly = false;

  /**
   * Determines whether the radio appears in a soft-disabled state.
   * Soft-disabled provides a less prominent disabled appearance than the standard disabled state.
   * Note: This is purely visual as StaticRadio is non-interactive by design.
   * @default false
   */
  @property({ type: Boolean, attribute: 'soft-disabled', reflect: true }) softDisabled = false;

  public override render() {
    return html` <slot></slot>
      <span part="radio-icon" class="radio-icon"></span>`;
  }

  public static override styles: Array<CSSResult> = [...Component.styles, ...styles];
}

export default StaticRadio;
